// src/phase4/systems/TDManager.ts
// TD Manager - Main coordinator for tower defense systems
// Uses preset bases where players place towers

import Phaser from 'phaser';
import { WaveManager } from './WaveManager';
import { TowerSystem } from './TowerSystem';
import { EnemySystem } from './EnemySystem';
import { TowerSprite, EnemySprite, BaseSprite, createTowerSprite } from '../entities';
import { createSoundManager, SoundManager } from '../audio';
import type { Project, SceneWithTD } from './TDManager.types';
import { TowerType } from '../types/tower';
import { EnemyType } from '../types/enemy';
import type { WaveSummary, WaveStatus } from '../types/wave';
import { PhaserUIManager } from '../ui/PhaserUIManager';
import { TowerPanelInfo, UpgradeInfo } from '../ui/TowerUpgradePanel';
import { getUpgradePath } from '../data/tower-configs';

// Re-export types for external use
export type { Project, SceneWithTD } from './TDManager.types';

// Base position configuration
interface BasePosition {
  id: string;
  x: number;
  y: number;
  allowedTypes?: ('CPU_MAIN' | 'LANGUAGE_SIDE' | 'NETWORK_SIDE')[];
}

export class TDManager {
  private scene: SceneWithTD;
  private tdContainer: Phaser.GameObjects.Container;

  // Legacy references for compatibility
  public enemies: Phaser.GameObjects.GameObject[] = [];
  public towers: Phaser.GameObjects.GameObject[] = [];
  public bullets: Phaser.GameObjects.GameObject[] = [];
  public path: Phaser.Curves.Path | null = null;

  // Subsystems
  private waveManager?: WaveManager;
  private towerSystem?: TowerSystem;
  private enemySystem?: EnemySystem;
  private soundManager?: SoundManager;
  private uiManager?: PhaserUIManager;

  // Visual entities
  private towerSprites: Map<string, TowerSprite> = new Map();
  private baseSprites: Map<string, BaseSprite> = new Map();
  private enemySprites: Map<string, EnemySprite> = new Map();
  private bulletSprites: Map<string, Phaser.GameObjects.GameObject> = new Map();

  // Event listeners (for cleanup)
  private baseSelectListener?: (data: { baseId: string; x: number; y: number; allowedTypes?: string[] }) => void;
  private pointerDownListener?: () => void;

  // State
  private isInitialized = false;
  private gameOverTriggered = false;
  private totalWaves: number = 7;

  // Preset base positions
  private readonly BASE_POSITIONS: BasePosition[] = [
    { id: 'base_1', x: 100, y: 200, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_2', x: 300, y: 100, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_3', x: 600, y: 100, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_4', x: 700, y: 200, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_5', x: 380, y: 320, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_6', x: 200, y: 420, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
    { id: 'base_7', x: 660, y: 420, allowedTypes: ['CPU_MAIN', 'LANGUAGE_SIDE', 'NETWORK_SIDE'] },
  ];

  constructor(scene: SceneWithTD, tdContainer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.tdContainer = tdContainer;
  }

  start(project: Project): void {
    console.log('[TDManager] start() called with project:', project);
    if (this.isInitialized) {
      console.log('[TDManager] Was already initialized, cleaning up first');
      this.cleanup();
    }

    // Create background image
    this.createBackground();

    // Initialize path
    this.path = this.createPath();

    // Initialize subsystems
    this.initSystems();

    // Initialize sound
    this.soundManager = createSoundManager(this.scene);

    // Initialize UI Manager (Pure Phaser UI system)
    this.initUIManager(project);

    // Create preset bases (empty platforms)
    this.createPresetBases();

    // Setup base interactions
    this.setupBaseInteractions();

    // Setup tower interactions
    this.setupTowerInteractions();

    console.log('[TDManager] tdContainer visible:', this.tdContainer.visible);
    console.log('[TDManager] tdContainer list length:', this.tdContainer.length);

    this.isInitialized = true;
    console.log('[TDManager] Tower defense initialized with preset bases');
  }

  private initUIManager(project: Project): void {
    // Create UI Manager
    this.uiManager = new PhaserUIManager(this.scene, this.tdContainer);

    // Setup UI callbacks
    this.uiManager.setTowerPlacedCallback((baseId, towerType, pathName) => {
      this.placeTowerInternal(baseId, towerType, pathName);
    });

    this.uiManager.setTowerUpgradedCallback((towerId) => {
      this.upgradeTowerInternal(towerId);
    });

    this.uiManager.setTowerSoldCallback((towerId) => {
      this.sellTowerInternal(towerId);
    });

    this.uiManager.setOnStartWaveCallback(() => {
      this.startNextWaveInternal();
    });

    this.uiManager.setOnPanelClosedCallback(() => {
      // Re-enable interaction and reset selection state for all towers when panel closes
      this.towerSprites.forEach((sprite) => {
        sprite.setInteractionEnabled(true);
        sprite.clearSelection();
      });
    });

    // Update displays with initial values
    this.uiManager.updateMoney(this.scene.money);
    this.uiManager.updateHealth(this.scene.baseHealth, 3);
    this.uiManager.updateWaveInfo(1, this.totalWaves);

    // Create title (outside UI manager for simplicity)
    const titleText = `DEFENDING: ${project.name}${project.region ? ` (${project.region})` : ''}`;
    const title = this.scene.add.text(400, 25, titleText, {
      fontSize: '20px',
      color: '#e05545',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.tdContainer.add(title);

    // Info text
    const infoText = this.scene.add.text(400, 530, 'Click empty bases to place towers', {
      fontSize: '14px',
      color: '#9b9488',
      fontFamily: 'monospace',
    }).setOrigin(0.5);
    this.tdContainer.add(infoText);

    // Exit button
    this.createExitButton();
  }

  private createExitButton(): void {
    const exitBtn = this.scene.add.text(750, 560, 'EXIT', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#e05545',
      backgroundColor: '#201e1a',
      padding: { x: 12, y: 8 },
    }).setOrigin(0.5);

    exitBtn.setInteractive({ useHandCursor: true })
      .on('pointerover', () => {
        exitBtn.setStyle({ backgroundColor: '#2a2824' });
      })
      .on('pointerout', () => {
        exitBtn.setStyle({ backgroundColor: '#201e1a' });
      })
      .on('pointerdown', () => {
        this.scene.sound.play('click');
        this.scene.returnToIdle();
      });

    this.tdContainer.add(exitBtn);
  }

  private createBackground(): void {
    // Check if background texture exists
    const textureKey = 'battle_map_bg';
    if (this.scene.textures.exists(textureKey)) {
      const bg = this.scene.add.image(400, 300, textureKey);
      bg.setOrigin(0.5, 0.5);
      // Scale to fit the screen if needed
      const scaleX = 800 / bg.width;
      const scaleY = 600 / bg.height;
      bg.setScale(Math.max(scaleX, scaleY));
      this.tdContainer.add(bg);
    }
  }

  private createPath(): Phaser.Curves.Path {
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(1, 0x3a362e);

    const path = new Phaser.Curves.Path(20, 150);
    path.lineTo(280, 150);
    path.lineTo(280, 400);
    path.lineTo(610, 400);
    path.lineTo(610, 150);
    path.lineTo(800, 150);

    path.draw(graphics);
    this.tdContainer.add(graphics);

    return path;
  }

  private initSystems(): void {
    if (!this.path) return;

    // Initialize WaveManager
    this.waveManager = new WaveManager(this.scene);
    this.waveManager.setSpawnCallback((enemy, x, y) => this.onEnemySpawn(enemy, x, y));
    this.waveManager.setWaveCompleteCallback((summary) => this.onWaveComplete(summary));
    this.waveManager.setAllWavesCompleteCallback(() => this.onAllWavesComplete());

    // Initialize EnemySystem
    this.enemySystem = new EnemySystem(this.scene, this.path);
    this.enemySystem.setDeathCallback((id, bounty) => this.onEnemyDeath(id, bounty));
    this.enemySystem.setEscapeCallback((id, damage) => this.onEnemyEscape(id, damage));
    this.enemySystem.setHitCallback((projectile, enemyId, damage) => this.onProjectileHit(projectile, enemyId, damage));

    // Initialize TowerSystem
    this.towerSystem = new TowerSystem(this.scene);
    this.towerSystem.setFireCallback((projectile) => this.onTowerFire(projectile));
    this.towerSystem.setUpgradeCallback((towerId, newLevel, cost) => this.onTowerUpgraded(towerId, newLevel, cost));
  }

  private createPresetBases(): void {
    console.log('[TDManager] createPresetBases called, creating', this.BASE_POSITIONS.length, 'bases');
    for (const baseConfig of this.BASE_POSITIONS) {
      console.log('[TDManager] Creating base:', baseConfig.id, 'at', baseConfig.x, baseConfig.y);
      const base = new BaseSprite(this.scene, {
        id: baseConfig.id,
        x: baseConfig.x,
        y: baseConfig.y,
        allowedTowerTypes: baseConfig.allowedTypes,
        scene: this.scene,
      });
      this.baseSprites.set(baseConfig.id, base);
      this.tdContainer.add(base);
      console.log('[TDManager] Base added to container, visible:', base.visible);
    }
    console.log('[TDManager] Total bases created:', this.baseSprites.size);
  }

  private setupBaseInteractions(): void {
    console.log('[TDManager] Setting up base interactions listener');

    // Listen for base selection events from BaseSprite
    this.baseSelectListener = (data: {
      baseId: string;
      x: number;
      y: number;
      allowedTypes?: string[];
    }) => {
      console.log('[TDManager] Received baseSelect event:', data);
      this.onBaseSelected(data.baseId, data.x, data.y);
    };
    this.scene.events.on('baseSelect', this.baseSelectListener!);

    // // Global pointerdown handler for bases
    // this.pointerDownListener = (pointer: Phaser.Input.Pointer) => {
    //   if (!this.tdContainer.visible) return;

    //   // Check if click is on any empty base
    //   for (const [baseId, base] of this.baseSprites) {
    //     if (base.isOccupied) continue;

    //     const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, base.getX(), base.getY());
    //     if (dist <= 32) {
    //       console.log('[TDManager] Global click detected on base:', baseId);
    //       this.onBaseSelected(baseId, base.getX(), base.getY());
    //       return;
    //     }
    //   }
    // };
    // this.scene.input.on('pointerdown', this.pointerDownListener);
  }

  private onBaseSelected(baseId: string, x: number, y: number): void {
    console.log('[TDManager] onBaseSelected called:', baseId, x, y);
    const base = this.baseSprites.get(baseId);
    if (!base || base.isOccupied) return;

    // Show tower selection panel via UI Manager
    this.uiManager?.showTowerSelect(baseId, x, y);
  }

  // Internal method called by UI Manager when tower is selected
  private placeTowerInternal(baseId: string, towerType: TowerType, pathName?: string): boolean {
    console.log('[TDManager] placeTowerInternal called:', baseId, towerType, pathName);
    const base = this.baseSprites.get(baseId);
    if (!base || base.isOccupied) return false;

    const towerId = `tower_${baseId}`;

    // Create tower in system
    this.towerSystem?.createTower(towerId, towerType, base.getX(), base.getY(), pathName);

    // Create tower sprite
    const towerSprite = createTowerSprite(
      this.scene,
      towerType,
      base.getX(),
      base.getY(),
      towerId,
      pathName
    );

    this.towerSprites.set(towerId, towerSprite);
    this.tdContainer.add(towerSprite);
    this.towers.push(towerSprite);

    // Mark base as occupied
    base.occupy();

    // Setup interaction for this tower
    towerSprite.on('select', ({ isSelected }: { tower: TowerSprite; isSelected: boolean }) => {
      if (isSelected) {
        this.soundManager?.playTowerSelect();
        this.onTowerSelected(towerId, towerSprite.getX(), towerSprite.getY());
      }
    });

    console.log(`[TDManager] Tower placed at base ${baseId}: ${towerType}`);
    this.soundManager?.playTowerUpgrade();

    return true;
  }

  // Handle tower selection for upgrade panel
  private onTowerSelected(towerId: string, x: number, y: number): void {
    const tower = this.towerSystem?.getTower(towerId);
    if (!tower) return;

    // Disable interaction for all towers when upgrade panel opens
    this.towerSprites.forEach((sprite) => {
      sprite.setInteractionEnabled(false);
    });

    // Re-enable the selected tower
    const selectedSprite = this.towerSprites.get(towerId);
    selectedSprite?.setInteractionEnabled(true);

    const pathName = this.getPathNameForTower(tower.type);
    const path = getUpgradePath(tower.type, pathName);
    const levelConfig = path?.levels[tower.currentLevel - 1];

    if (!levelConfig) return;

    const towerInfo: TowerPanelInfo = {
      id: towerId,
      type: tower.type,
      name: levelConfig.name,
      level: tower.currentLevel,
      dps: levelConfig.dps,
      range: levelConfig.range,
      fireRate: levelConfig.fireRate,
      specialEffects: levelConfig.specialEffects?.map(e => e.type),
      pathName,
    };

    const upgradeInfo = this.towerSystem?.getUpgradeInfo(towerId);
    if (upgradeInfo) {
      const nextConfig = path?.levels[tower.currentLevel];
      const info: UpgradeInfo = {
        canUpgrade: upgradeInfo.canUpgrade,
        cost: upgradeInfo.cost,
        nextLevel: upgradeInfo.nextLevel,
        dpsIncrease: nextConfig ? nextConfig.dps - levelConfig.dps : undefined,
        rangeIncrease: nextConfig ? nextConfig.range - levelConfig.range : undefined,
        newEffects: nextConfig?.specialEffects?.map(e => e.type),
      };

      this.uiManager?.showTowerUpgrade(towerId, x, y, towerInfo, info);
    }
  }

  // Internal upgrade method called by UI Manager
  private upgradeTowerInternal(towerId: string): boolean {
    // Check if tower can be upgraded and get cost
    const upgradeInfo = this.towerSystem?.getUpgradeInfo(towerId);
    if (!upgradeInfo || !upgradeInfo.canUpgrade) {
      return false;
    }

    // Check if player has enough money
    if (this.scene.money < upgradeInfo.cost) {
      console.log(`[TDManager] Not enough money to upgrade tower. Need ${upgradeInfo.cost}, have ${this.scene.money}`);
      return false;
    }

    // Deduct upgrade cost
    this.scene.money -= upgradeInfo.cost;
    this.uiManager?.updateMoney(this.scene.money);

    // Perform the upgrade
    const result = this.towerSystem?.upgradeTower(towerId);
    if (result) {
      const tower = this.towerSystem?.getTower(towerId);
      if (tower) {
        const sprite = this.towerSprites.get(towerId);
        if (sprite) {
          sprite.updateLevel(tower.currentLevel);
        }
        // Update upgrade panel if still open
        this.onTowerSelected(towerId, sprite?.x || 0, sprite?.y || 0);
      }
    }
    return result || false;
  }

  // Internal start wave method called by UI Manager
  private startNextWaveInternal(): boolean {
    this.uiManager?.setWaveActive(true);
    return this.waveManager?.startNextWave() || false;
  }

  // Internal sell tower method called by UI Manager
  private sellTowerInternal(towerId: string): boolean {
    const refund = this.towerSystem?.sellTower(towerId) || 0;

    if (refund > 0) {
      // Add refund to player's money
      this.scene.money += refund;
      this.uiManager?.updateMoney(this.scene.money);
      console.log(`[TDManager] Tower ${towerId} sold for ${refund} FLOP`);
    }

    // Remove tower sprite
    const sprite = this.towerSprites.get(towerId);
    if (sprite) {
      sprite.destroy();
      this.towerSprites.delete(towerId);
    }

    // Extract baseId from towerId (format: tower_{baseId})
    const baseId = towerId.replace('tower_', '');

    // Vacate the base so a new tower can be placed
    const base = this.baseSprites.get(baseId);
    if (base) {
      base.vacate();
      console.log(`[TDManager] Base ${baseId} is now vacant`);
    }

    // Hide upgrade panel
    this.uiManager?.hideTowerUpgrade();

    return true;
  }

  // Public method to place a tower on a base (legacy)
  placeTower(baseId: string, towerType: TowerType, pathName?: string): boolean {
    return this.placeTowerInternal(baseId, towerType, pathName);
  }

  private setupTowerInteractions(): void {
    // Handled individually when towers are placed
  }

  private getPathNameForTower(type: TowerType): string {
    switch (type) {
      case TowerType.CPU_MAIN:
        return 'CPU';
      case TowerType.LANGUAGE_SIDE:
        return 'C';
      case TowerType.NETWORK_SIDE:
        return 'Apache';
      default:
        return 'CPU';
    }
  }

  update(delta: number): void {
    if (!this.isInitialized) return;

    const currentTime = this.scene.time.now;

    // Update WaveManager (spawns enemies)
    this.waveManager?.update(delta);

    // Update EnemySystem (moves enemies)
    this.enemySystem?.update(delta);

    // Get active enemies for tower targeting
    const activeEnemies = this.enemySystem?.getActiveEnemies() || [];

    // Update TowerSystem (finds targets and fires)
    this.towerSystem?.update(delta, activeEnemies, currentTime);

    // Update bullet sprites
    this.updateBullets(delta);

    // Sync enemy sprites with system
    this.syncEnemySprites();

    // Check game over condition (only trigger once)
    if (this.scene.baseHealth <= 0 && !this.gameOverTriggered) {
      this.handleGameOver();
    }
  }

  private syncEnemySprites(): void {
    const activeEnemies = this.enemySystem?.getActiveEnemies() || [];

    this.enemySprites.forEach((sprite, id) => {
      const enemy = activeEnemies.find((e) => e.id === id);
      if (enemy) {
        sprite.updatePosition(enemy.x, enemy.y);
        sprite.updateHealth(enemy.currentHp);
        sprite.setStealthed(enemy.isStealthed);
      }
    });
  }

  private updateBullets(_delta: number): void {
    // Update bullet positions and check collisions
    // This is handled by EnemySystem.applyDamage in onTowerFire
  }

  // ==================== Event Handlers ====================

  private onEnemySpawn(enemy: { id: string; type: EnemyType }, x: number, y: number): void {
    // Create enemy sprite
    const config = {
      id: enemy.id,
      type: enemy.type,
      x,
      y,
      maxHp: 100,
      scene: this.scene,
    };

    const sprite = new EnemySprite(this.scene, config);
    this.enemySprites.set(enemy.id, sprite);
    this.tdContainer.add(sprite);
    this.enemies.push(sprite);

    // Register with enemy system
    this.enemySystem?.registerEnemy(sprite.getInstance());

    this.soundManager?.playEnemySpawn();
  }

  private onTowerFire(projectile: {
    id: string;
    targetId: string;
    x: number;
    y: number;
    damage: number;
    speed: number;
    towerType: TowerType;
  }): void {
    // Play tower shoot sound based on tower type
    const towerTypeKey = projectile.towerType === TowerType.CPU_MAIN ? 'cpu'
      : projectile.towerType === TowerType.LANGUAGE_SIDE ? 'lang'
      : 'net';
    this.soundManager?.playTowerShoot(towerTypeKey);

    const target = this.enemySprites.get(projectile.targetId);
    if (!target) return;

    // Store tower position (fixed)
    const towerX = projectile.x;
    const towerY = projectile.y;

    // Create laser effect - draw line from tower to target
    const laserGraphics = this.scene.add.graphics();
    this.tdContainer.add(laserGraphics);

    // Draw laser function
    const drawLaser = () => {
      laserGraphics.clear();
      // Get current target position
      const endX = target.x;
      const endY = target.y;

      // Outer glow
      laserGraphics.lineStyle(8, 0xffaa00, 0.3);
      laserGraphics.beginPath();
      laserGraphics.moveTo(towerX, towerY);
      laserGraphics.lineTo(endX, endY);
      laserGraphics.strokePath();
      // Inner beam
      laserGraphics.lineStyle(3, 0xff6600, 0.9);
      laserGraphics.beginPath();
      laserGraphics.moveTo(towerX, towerY);
      laserGraphics.lineTo(endX, endY);
      laserGraphics.strokePath();
      // Core
      laserGraphics.lineStyle(1, 0xffff00, 1);
      laserGraphics.beginPath();
      laserGraphics.moveTo(towerX, towerY);
      laserGraphics.lineTo(endX, endY);
      laserGraphics.strokePath();
    };

    drawLaser();

    // Laser duration - short flash
    const laserDuration = 100;

    // Use a timer to update laser position while it's active
    const laserTimer = this.scene.time.addEvent({
      delay: 16, // ~60fps
      repeat: Math.floor(laserDuration / 16) - 1,
      callback: () => {
        drawLaser();
      },
    });

    // Apply damage immediately (laser hits instantly)
    const result = this.enemySystem?.applyDamage({
      id: projectile.id,
      towerId: '',
      targetId: projectile.targetId,
      x: projectile.x,
      y: projectile.y,
      damage: projectile.damage,
      speed: projectile.speed,
      hasSlowEffect: false,
      hasPierceEffect: false,
      isCritical: false,
      towerType: projectile.towerType,
    });

    if (result) {
      const targetSprite = this.enemySprites.get(projectile.targetId);
      targetSprite?.playHitAnimation();
      this.soundManager?.playEnemyDamage();

      if (result.enemyKilled) {
        // Notify wave manager that enemy was killed
        this.waveManager?.enemyKilled(projectile.targetId, projectile.damage);

        // Handle death animation
        targetSprite?.playDeathAnimation().then(() => {
          this.enemySprites.delete(projectile.targetId);
          const index = this.enemies.findIndex((e) => e === targetSprite);
          if (index > -1) this.enemies.splice(index, 1);
        });
      }
    }

    // Fade out and destroy laser
    this.scene.time.delayedCall(laserDuration, () => {
      laserTimer.remove();
      laserGraphics.destroy();
    });
  }

  private onEnemyDeath(_enemyId: string, bounty: number): void {
    this.scene.money += bounty;
    this.uiManager?.updateMoney(this.scene.money);
    console.log(`[TDManager] Enemy killed, reward: ${bounty} FLOP`);
  }

  private onEnemyEscape(enemyId: string, damage: number): void {
    this.scene.baseHealth -= damage;
    this.uiManager?.updateHealth(this.scene.baseHealth, 3);
    console.log(`[TDManager] Enemy escaped, damage: ${damage}`);
    this.soundManager?.playBaseHit();

    // Notify wave manager
    this.waveManager?.enemyEscaped(enemyId);

    // Remove enemy sprite
    const enemySprite = this.enemySprites.get(enemyId);
    if (enemySprite) {
      enemySprite.destroy();
      this.enemySprites.delete(enemyId);
      const index = this.enemies.findIndex((e) => e === enemySprite);
      if (index > -1) this.enemies.splice(index, 1);
    }
  }

  private onProjectileHit(
    _projectile: { id: string },
    _enemyId: string,
    _damageDealt: number
  ): void {
    // Visual feedback handled in onTowerFire
  }

  private onWaveComplete(summary: WaveSummary): void {
    console.log(`[TDManager] Wave ${summary.waveNumber} complete:`, summary);
    this.soundManager?.playWaveComplete();

    // Award wave completion rewards
    this.scene.money += summary.rewards.flops;
    this.uiManager?.updateMoney(this.scene.money);

    // Update wave info for next wave
    const nextWave = summary.waveNumber + 1;
    if (nextWave <= this.totalWaves) {
      this.uiManager?.updateWaveInfo(nextWave, this.totalWaves);
      this.uiManager?.setWaveActive(false);
    }
  }

  private onAllWavesComplete(): void {
    console.log('[TDManager] All waves complete!');
    this.soundManager?.playWaveAllComplete();

    // Victory condition
    this.scene.events.emit('victory');
  }

  private onTowerUpgraded(towerId: string, newLevel: number, _cost: number): void {
    console.log(`[TDManager] Tower ${towerId} upgraded to level ${newLevel}`);
    this.soundManager?.playTowerUpgrade();

    const sprite = this.towerSprites.get(towerId);
    if (sprite) {
      sprite.updateLevel(newLevel);
    }
  }

  private handleGameOver(): void {
    this.gameOverTriggered = true;
    console.log('[TDManager] Game over - base health depleted');
    this.soundManager?.play('game_over');
    this.scene.events.emit('gameOver');
  }

  // ==================== Public API ====================

  // Start next wave manually
  startNextWave(): boolean {
    return this.waveManager?.startNextWave() || false;
  }

  // Upgrade a tower
  upgradeTower(towerId: string): boolean {
    return this.towerSystem?.upgradeTower(towerId) || false;
  }

  // Get tower info for UI
  getTowerInfo(towerId: string) {
    const tower = this.towerSystem?.getTower(towerId);
    const stats = this.towerSystem?.getTowerStats(towerId);
    const upgradeInfo = this.towerSystem?.getUpgradeInfo(towerId);
    const sprite = this.towerSprites.get(towerId);

    return {
      tower,
      stats,
      upgradeInfo,
      sprite,
    };
  }

  // Get wave status
  getWaveStatus(): WaveStatus | undefined {
    return this.waveManager?.getStatus();
  }

  // Get battle statistics
  getBattleStats(): { enemiesKilled: number; wavesCompleted: number; currentWave: number; totalWaves: number } {
    const waveStatus = this.waveManager?.getStatus();
    const currentWave = waveStatus?.waveNumber || 0;
    // wavesCompleted is currentWave - 1 (since current wave may not be complete yet)
    const wavesCompleted = waveStatus?.isComplete ? currentWave : currentWave - 1;

    return {
      enemiesKilled: this.waveManager?.getTotalEnemiesKilled() || 0,
      wavesCompleted: Math.max(0, wavesCompleted),
      currentWave,
      totalWaves: this.totalWaves,
    };
  }

  // Get UI Manager for external access
  getUIManager(): PhaserUIManager | undefined {
    return this.uiManager;
  }

  // Get number of towers currently built
  getTowersBuiltCount(): number {
    return this.towerSprites.size;
  }

  // Get available bases info
  getAvailableBases() {
    const available: { id: string; x: number; y: number; allowedTypes?: string[] }[] = [];
    this.baseSprites.forEach((base, id) => {
      if (!base.isOccupied) {
        available.push({
          id,
          x: base.getX(),
          y: base.getY(),
        });
      }
    });
    return available;
  }

  // Cleanup
  cleanup(): void {
    // Remove event listeners first (before destroying sprites)
    if (this.baseSelectListener) {
      this.scene.events.off('baseSelect', this.baseSelectListener);
      this.baseSelectListener = undefined;
    }
    if (this.pointerDownListener) {
      this.scene.input.off('pointerdown', this.pointerDownListener);
      this.pointerDownListener = undefined;
    }

    // Destroy UI Manager first
    this.uiManager?.destroy();
    this.uiManager = undefined;

    // Destroy sprites
    this.towerSprites.forEach((sprite) => sprite.destroy());
    this.baseSprites.forEach((sprite) => sprite.destroy());
    this.enemySprites.forEach((sprite) => sprite.destroy());
    this.bulletSprites.forEach((sprite) => sprite.destroy());

    this.towerSprites.clear();
    this.baseSprites.clear();
    this.enemySprites.clear();
    this.bulletSprites.clear();

    // Clear arrays
    this.enemies = [];
    this.towers = [];
    this.bullets = [];

    // Destroy subsystems
    this.soundManager?.destroy();
    this.waveManager?.reset();
    this.towerSystem?.reset();
    this.enemySystem?.reset();

    this.isInitialized = false;
  }

  // Destroy
  destroy(): void {
    this.cleanup();
  }
}
